import { usePublishLanguage } from '@src/hooks/usePublishLanguage';
import { VideoDeletionService } from '@src/services/VideoDeletionService';
import { useEffect, useMemo, useRef, useState } from 'react';

import { VideoResolution } from '../../../types.ts/video';
import { extractErrorInfo } from '../../../utility/error';
import { LogManager } from '../../../utility/Logger';
import { getFormattedDurationForStoryVideo } from '../../../utility/timeFormat';

import type {
  AssociatedVideo,
  StoryVideo,
  VideoAction,
} from '../../../types.ts/story';
import type { AspectRatio } from '../../../types.ts/video';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

export type DropdownOption = {
  id: string;
  title: string;
  duration?: number | null;
  videoFilePrimary?: StoryVideo;
  associatedVideos?: AssociatedVideo[];
  aspectRatio?: AspectRatio;
  isHidden?: boolean;
  lastAction?: VideoAction;
  isClientReady?: boolean;
};

interface UseVideoSelectorProps {
  videoCreator: IVideoCreatorStore;
  renderUsingChunks: boolean;
}

const logger = LogManager.getLogger('hook:useVideoSelector');

export const useVideoSelector = ({
  videoCreator,
  renderUsingChunks,
}: UseVideoSelectorProps) => {
  const publishLanguage = usePublishLanguage();
  // Services
  const deletionService = useMemo(
    () => new VideoDeletionService({ videoCreator }),
    [videoCreator],
  );

  // State
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [newVideoModal, setNewVideoModal] = useState<{
    type: 'save_as' | 'rename';
    video: DropdownOption | null;
  } | null>(null);
  const [actionProgress, setActionProgress] = useState<string>('');
  const [editingCurrentVideo, setEditingCurrentVideo] =
    useState<boolean>(false);
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string>('');
  const [optionsModal, setOptionsModal] = useState<{
    position: number;
    right: number;
    video: DropdownOption | null;
  } | null>(null);
  const [isMouseDown, setIsMouseDown] = useState<boolean>(false);
  const [videoToDelete, setVideoToDelete] = useState<DropdownOption | null>(
    null,
  );

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const editingCurrentVideoRef = useRef<boolean>(false);

  // Helper function to get the most recent action date for a video
  const getMostRecentActionDate = (video: DropdownOption): number => {
    const allLastActions = [
      video.lastAction,
      ...(video.associatedVideos?.map(v => v.lastActionJson) || []),
    ].filter(Boolean) as VideoAction[];

    if (!allLastActions.length) return 0; // Videos without actions go to the end

    const mostRecent = allLastActions.reduce((latest, current) =>
      !latest || new Date(current.date) > new Date(latest.date)
        ? current
        : latest,
    );

    return new Date(mostRecent.date).getTime();
  };

  // Helper function to sort videos by most recent modification date
  const sortVideosByMostRecent = (
    videos: DropdownOption[],
  ): DropdownOption[] => {
    return videos.sort((a, b) => {
      const dateA = getMostRecentActionDate(a);
      const dateB = getMostRecentActionDate(b);
      return dateB - dateA; // Descending order (most recent first)
    });
  };

  // Prepare current video option
  const getCurrentVideoOption = (): DropdownOption | null => {
    const currentParentVideo =
      videoCreator.currentVideo?.parentVideo || videoCreator.currentVideo;
    return currentParentVideo
      ? ({
          id: currentParentVideo.id,
          title: currentParentVideo.title,
          duration:
            getFormattedDurationForStoryVideo(currentParentVideo).duration,
          videoFilePrimary: currentParentVideo.videoFilePrimary,
          associatedVideos: currentParentVideo.associatedVideos,
          aspectRatio: currentParentVideo.aspectRatio,
          isHidden: currentParentVideo.isHidden,
          lastAction: currentParentVideo.lastActionJson,
          isClientReady: currentParentVideo.isClientReady,
        } as DropdownOption)
      : null;
  };

  /**
   * The name actually on screen. The clip's own title where it has one, and the
   * story's name where it does not — the fallback `MainTitle` renders, so the
   * rename opens on the text the user is looking at rather than on an empty box
   * (PLAT-11503). The title renders it (`displayedTitle`) and the rename
   * pre-fills, resets and commits against it, so they can't drift apart again.
   */
  const getDisplayedTitle = (): string =>
    getCurrentVideoOption()?.title || videoCreator.storyName || '';

  // Memoized video options data
  const allVideoOptions = useMemo(() => {
    const currentVideoOption = getCurrentVideoOption();

    const originalStoryOption = {
      id: 'original_story',
      title: videoCreator.story?.title,
      duration: videoCreator.story?.originalVideo?.video.duration,
    } as DropdownOption;

    let videoOptions: DropdownOption[] = [];
    if (currentVideoOption && !currentVideoOption.id) {
      videoOptions.push(currentVideoOption);
    }
    if (videoCreator.story?.otherVideos?.length) {
      videoOptions = videoOptions.concat(
        videoCreator.story?.otherVideos.map((video: any) => ({
          id: video.id!,
          title: video.title,
          duration: getFormattedDurationForStoryVideo(video).duration,
          videoFilePrimary: video.videoFilePrimary,
          associatedVideos: video.associatedVideos,
          aspectRatio: video.aspectRatio,
          isHidden: video.isHidden,
          lastAction: video.lastActionJson,
          isClientReady: video.isClientReady,
        })),
      );
    }

    videoOptions.push(originalStoryOption);

    // Sort videos by most recent modification date, but keep current video pinned at top
    const currentVideoId = currentVideoOption?.id;
    if (currentVideoId) {
      // Pin current video at top, sort the rest by most recent
      const currentVideo = videoOptions.find(
        video => video.id === currentVideoId,
      );
      const otherVideos = videoOptions.filter(
        video => video.id !== currentVideoId,
      );
      const sortedOtherVideos = sortVideosByMostRecent(otherVideos);
      videoOptions = currentVideo
        ? [currentVideo, ...sortedOtherVideos]
        : sortedOtherVideos;
    } else {
      // No current video, sort all by most recent
      videoOptions = sortVideosByMostRecent(videoOptions);
    }

    return videoOptions;
  }, [
    videoCreator.currentVideo?.id,
    videoCreator.currentVideo?.parentVideo?.id,
    videoCreator.currentVideo?.title,
    videoCreator.currentVideo?.parentVideo?.title,
    videoCreator.currentVideo?.associatedVideos?.length,
    videoCreator.currentVideo?.parentVideo?.associatedVideos?.length,
    videoCreator.story?.otherVideos,
    videoCreator.story?.otherVideos
      ?.map(v => v.associatedVideos?.length)
      .join(','),
    videoCreator.story?.title,
    videoCreator.story?.originalVideo?.video.duration,
  ]);

  // Mutating functions
  const copyVideo = async (video: DropdownOption, newTitle: string) => {
    if (video.id !== 'original_story') {
      await videoCreator.copyVideo(video.id, newTitle, 'creator-studio');
    } else {
      await videoCreator.createNewVideoFromSource(newTitle, true);
    }
    videoCreator.addUnsaveVideoAction();
  };

  const deleteSelectedVideos = async (
    selectedVideoIds: string[],
    targetVideo: DropdownOption | null,
  ) => {
    if (selectedVideoIds.length === 0 || !targetVideo || !targetVideo.id) {
      return;
    }

    const result = await deletionService.deleteSelectedVideos(
      selectedVideoIds,
      targetVideo.id,
      targetVideo.associatedVideos || [],
      allVideoOptions,
    );

    if (!result.ok) {
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to delete selected clip versions.',
        delay: 3000,
      });
      logger.error(
        'Failed to delete selected videos',
        extractErrorInfo(result.error),
      );
      return;
    }

    if (result.value.switchedToNextVideo) {
      selectVideo(result.value.nextVideoId);
    }

    videoCreator.showToast({
      state: 'success',
      message: 'Selected clip versions deleted.',
      delay: 3000,
    });
  };

  const makeFeatureVideo = async (videoId: string) => {
    try {
      setOptionsModal(null);

      if (videoId === 'original_story') {
        await videoCreator.createNewVideoFromSource(undefined, true);
      } else if (videoId && videoId !== videoCreator.currentVideo?.id) {
        await videoCreator.loadVideo(videoId);
      }

      await videoCreator.saveStoryAndVideo(true);
      await videoCreator.publishVideo(
        VideoResolution.High,
        renderUsingChunks,
        publishLanguage,
      );
    } catch (error) {
      console.error('Error making feature video:', error);
    }
  };

  const selectVideo = (videoId: string) => {
    const currentVideoOption = allVideoOptions.find(
      v => v.id === videoCreator.currentVideo?.id,
    );

    if (videoId === currentVideoOption?.id) {
      return;
    } else if (videoId === 'original_story') {
      videoCreator.createNewVideoFromSource(undefined, true);
      videoCreator.addUnsaveVideoAction();
    } else {
      videoCreator.loadVideo(videoId);
    }
  };

  const handleRenameCurrentVideo = async () => {
    // Only save when the inline title editor is actually open. Item's onBlur
    // also fires when the dropdown chevron (inside the same wrapper) loses
    // focus on outside-click; without this guard that path renames the clip to
    // the empty currentVideoTitle state. (PLAT-10711)
    if (!editingCurrentVideoRef.current) return;
    // Parent-aware lookup — a loaded child variant carries its own id, while
    // the dropdown options (and the displayed title) use the parent's.
    const currentVideoOption = getCurrentVideoOption();
    if (!currentVideoOption?.id) return;
    const title = currentVideoTitle.trim();
    // A clip can't be nameless — the Content Studio clip list has nothing to
    // render for one — so an emptied field snaps back, as the clip card's and
    // the reel title's renames do.
    if (!title) return;
    // Against the name that was shown, not the stored one. The field opens on
    // the story-name fallback when the clip has none of its own, so comparing
    // with the stored title would save that fallback as a hand-typed name the
    // moment the field closed untouched — the same rule InlineRenameField keeps.
    if (title !== getDisplayedTitle()) {
      setActionProgress('Renaming video...');
      // Both callers fire this without awaiting it. `renameVideo` has already
      // put the old title back by the time it throws.
      try {
        await videoCreator.renameVideo(currentVideoOption.id, title);
      } catch (error) {
        videoCreator.showToast({
          state: 'error',
          message: 'Failed to rename video.',
          delay: 3000,
        });
        logger.error('Failed to rename video', extractErrorInfo(error));
      } finally {
        setActionProgress('');
      }
    }
  };

  // Event handlers
  const handleShowEditInput = () => {
    editingCurrentVideoRef.current = true;
    setEditingCurrentVideo(true);
    setDropdownOpen(false);
  };

  const toggleEditingInput = () => {
    if (isMouseDown) return;
    setEditingCurrentVideo(false);
    editingCurrentVideoRef.current = false;
  };

  // Effects
  useEffect(() => {
    if (!editingCurrentVideoRef.current) return;
    // The name the user sees, fallback included, so the input opens pre-filled
    // rather than on an empty box.
    setCurrentVideoTitle(getDisplayedTitle());
    // Never take focus off the renderer iframe: the iframe-deactivation reaction
    // dispatches FORCED_REFRESH, which can revert an in-flight canvas edit
    // (CLAUDE.md pitfall 10, PLAT-10700) — and the canvas is directly below this
    // toolbar. A click on the pencil has already blurred the iframe, so in
    // practice this only skips the autofocus for a programmatic open. Mirrors the
    // guard in InlineRenameField.
    if (document.activeElement instanceof HTMLIFrameElement) return;
    inputRef.current?.focus();
    // Select-all on entry, matching the Articles title rename interaction.
    inputRef.current?.select();
  }, [editingCurrentVideoRef.current, inputRef.current, allVideoOptions]);

  const handleToggleEditingVideo = async (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      if (!isMouseDown) toggleEditingInput();
    }
  };

  const handleMouseDown = () => {
    setIsMouseDown(true);
  };

  const handleMouseUp = (event: MouseEvent) => {
    if (
      inputRef.current &&
      !inputRef.current.contains(event.target as Node) &&
      !window.getSelection()?.toString()
    ) {
      setIsMouseDown(false);
    }
  };

  useEffect(() => {
    window.addEventListener('click', handleToggleEditingVideo);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('click', handleToggleEditingVideo);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMouseDown]);

  return {
    // State
    dropdownOpen,
    setDropdownOpen,
    newVideoModal,
    setNewVideoModal,
    actionProgress,
    setActionProgress,
    editingCurrentVideo,
    setEditingCurrentVideo,
    currentVideoTitle,
    setCurrentVideoTitle,
    optionsModal,
    setOptionsModal,
    isMouseDown,
    setIsMouseDown,
    videoToDelete,
    setVideoToDelete,

    // Refs
    inputRef,
    containerRef,
    buttonRef,
    editingCurrentVideoRef,

    // Data
    allVideoOptions,
    currentVideoOption: getCurrentVideoOption(),
    displayedTitle: getDisplayedTitle(),
    getMostRecentActionDate,

    // Actions
    copyVideo,
    deleteSelectedVideos,
    makeFeatureVideo,
    selectVideo,
    handleRenameCurrentVideo,

    // Event handlers
    handleShowEditInput,
    toggleEditingInput,
  };
};
